import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CenterSpinner } from "@/components/common/States";
import { cn } from "@/lib/utils";

const TABS = [
  { kind: "policy", label: "Privacy Policy" },
  { kind: "terms", label: "Terms & Conditions" },
  { kind: "paia", label: "PAIA Manual" },
  { kind: "cookies", label: "Cookies" },
];

export default function LegalDoc() {
  const { kind = "policy" } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    setDoc(null);
    api(`/privacy/documents/${kind}`).then(setDoc).catch(() => setDoc({ error: true }));
    api("/privacy/config").then((c) => setCompany(c.company)).catch(() => {});
  }, [kind]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><Brand /></Link>
          <Button variant="ghost" className="ml-auto rounded-full" onClick={() => navigate(-1)} data-testid="legal-back">Back</Button>
        </div>
      </header>
      <div className="checker checker-red checker-sm h-2 w-full" />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <Link
              key={t.kind}
              to={`/legal/${t.kind}`}
              data-testid={`legal-tab-${t.kind}`}
              className={cn("rounded-full border-2 px-3.5 py-1.5 text-sm font-medium", kind === t.kind ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted")}
            >
              {t.label}
            </Link>
          ))}
        </div>

        {!doc ? (
          <CenterSpinner />
        ) : doc.error ? (
          <p className="text-muted-foreground">Document unavailable.</p>
        ) : (
          <article data-testid="legal-document">
            <h1 className="font-display text-3xl">{doc.title}</h1>
            {(doc.version || doc.effective) && (
              <p className="mt-1 text-sm text-muted-foreground">
                {doc.version ? `Version ${doc.version}` : ""}{doc.effective ? ` · Effective ${doc.effective}` : ""}
              </p>
            )}
            {doc.legal_review_required && (
              <div className="mt-4 rounded-xl border-2 border-dashed border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-3 text-sm" data-testid="legal-review-banner">
                Draft for review — this document contains placeholders and must be reviewed by a qualified South African legal professional before production.
              </div>
            )}
            <div className="mt-6 space-y-5">
              {doc.sections?.map((s, i) => (
                <section key={i}>
                  <h2 className="font-display text-lg">{s.h}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.b}</p>
                </section>
              ))}
            </div>
            {company && (
              <Card className="mt-8 rounded-2xl p-5 text-sm">
                <p className="font-display text-base">{company.legal_name}</p>
                <p className="text-muted-foreground">{company.physical_address}</p>
                <p className="mt-2 text-muted-foreground">
                  Information Officer: {company.information_officer_name} ·{" "}
                  <a className="text-primary" href={`mailto:${company.information_officer_email}`}>{company.information_officer_email}</a>
                </p>
              </Card>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
